/**
 * Route↔label mapping for the App Shell (V2 Route) Left Nav.
 *
 * The App Shell is mounted under `<BrowserRouter basename="/v2">`
 * (see `AppShell.tsx`), so every `path` value exported from this module is
 * expressed **relative to that basename** — i.e. WITHOUT the `/v2` prefix.
 * The four values exposed here therefore correspond to the following
 * user-visible URL paths:
 *
 * | Router path (this module) | Browser URL      | Display label       |
 * | ------------------------- | ---------------- | ------------------- |
 * | `/search`                 | `/v2/search`     | "ASIN Search"       |
 * | `/baseline`               | `/v2/baseline`   | "ASIN Date Baseline"|
 * | `/validation`             | `/v2/validation` | "Validation"        |
 * | `/launch`                 | `/v2/launch`     | "Launch"            |
 *
 * The order of {@link LEFT_NAV_ITEMS} is significant and matches the order
 * mandated by Requirement 1 AC 2 ("ASIN Search", "ASIN Date Baseline",
 * "Validation", "Launch").
 *
 */

/**
 * A router-facing path for one of the four primary Left Nav items,
 * expressed relative to the App Shell's `basename="/v2"`.
 */
export type LeftNavPath = "/search" | "/baseline" | "/validation" | "/launch";

/**
 * The four tab identifiers that address a sub-view of the Validation
 * Dashboard on both the V2 Route and the Legacy Route. Kept alongside
 * {@link LeftNavPath} for a single source of truth for App-Shell-level
 * URL vocabulary.
 *
 * @see Requirement R6 AC 1
 */
export type ValidationTabId =
  | "dashboard"
  | "step-breakdown"
  | "program-tickets"
  | "catalog-issues";

/**
 * The default Left Nav path the App Shell activates when the V2 Route is
 * loaded with a path suffix that does not match any of the four primary
 * Left Nav items (R1 AC 9).
 */
export const DEFAULT_LEFT_NAV_PATH: LeftNavPath = "/search";

/**
 * Ordered list of the four primary Left Nav items. The order is
 * significant and matches the order mandated by Requirement 1 AC 2:
 * "ASIN Search", "ASIN Date Baseline", "Validation", "Launch". Consumers
 * that iterate the Left Nav (e.g. `LeftNav.tsx`, `AppBreadcrumbs.tsx`)
 * MUST iterate this array so that the rendered order stays in sync with
 * the requirements.
 */
export const LEFT_NAV_ITEMS: ReadonlyArray<{
  readonly path: LeftNavPath;
  readonly label: string;
}> = [
  { path: "/search", label: "ASIN Search" },
  { path: "/baseline", label: "ASIN Date Baseline" },
  { path: "/validation", label: "Validation" },
  { path: "/launch", label: "Launch" },
];

/**
 * Reverse lookup from a router-facing {@link LeftNavPath} to the
 * corresponding display label. Derived from {@link LEFT_NAV_ITEMS} so
 * that the two never drift.
 */
export const LEFT_NAV_LABELS: Readonly<Record<LeftNavPath, string>> =
  LEFT_NAV_ITEMS.reduce((acc, item) => {
    acc[item.path] = item.label;
    return acc;
  }, {} as Record<LeftNavPath, string>);

/**
 * Set of known {@link LeftNavPath} values, used internally by
 * {@link resolveLeftNavPath} for O(1) membership checks. Kept private to
 * this module.
 */
const KNOWN_PATHS: ReadonlySet<string> = new Set(
  LEFT_NAV_ITEMS.map((item) => item.path)
);

/**
 * Returns the {@link LeftNavPath} the App Shell should redirect to for
 * the given path suffix.
 *
 * The `suffix` is expected to be the pathname portion of the URL as seen
 * by the router — i.e. relative to `basename="/v2"`. Callers can pass
 * either a bare segment (e.g. `"/search"`, `"/unknown"`) or a longer
 * pathname (e.g. `"/search/details/B0..."`); only the first segment is
 * inspected.
 *
 * When the first segment matches one of the four known Left Nav paths,
 * that path is returned unchanged. Otherwise (empty suffix, `/`, or any
 * unknown suffix), {@link DEFAULT_LEFT_NAV_PATH} (`/search`) is returned.
 *
 * The returned value never includes the `/v2` prefix and is therefore
 * suitable for direct use as the `to` prop of `<Navigate/>` and the
 * argument of `useNavigate()(...)` inside the App Shell's router.

 */
export function resolveLeftNavPath(suffix: string): LeftNavPath {
  // Normalise: strip any leading slashes, then keep only the first
  // segment. This lets `resolveLeftNavPath("/search")`,
  // `resolveLeftNavPath("search")` and
  // `resolveLeftNavPath("/search/anything")` all resolve to `/search`.
  const firstSegment = suffix.replace(/^\/+/, "").split("/")[0];
  const candidate = `/${firstSegment}`;
  if (KNOWN_PATHS.has(candidate)) {
    return candidate as LeftNavPath;
  }
  return DEFAULT_LEFT_NAV_PATH;
}
